import { EditIcon } from "@xola/icons";
import { Input } from "@xola/ui-kit";
import { FormField, isPurchasePhoneRequired, SecondaryButton, SwitchField } from "common";
import { useField } from "formik";
import React, { useEffect } from "react";
import { FieldType, UserDetailsRequirement } from "types";
import shallow from "zustand/shallow";
import { debouncedSyncGuestFormResponse } from "./helpers/PurchaseCreateHelpers";
import { useGiftOnlyOrganizerRequirement } from "./hooks/useGiftDetailsRequirements";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { useSeller } from "@/hooks/useAccount";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export const CreatePurchasePhoneNumber = React.memo(({ onEditContactClick }: { onEditContactClick: () => void }) => {
    const seller = useSeller();
    const [field, , helpers] = useField("phone");

    const [organizer, form] = useCreatePurchaseStore((state) => [state.organizer, state.getForm()], shallow);
    const phoneRequired = useCustomerDetailsStore((state) => state.phoneRequired);

    const giftOrganizerRequirement = useGiftOnlyOrganizerRequirement();
    const requirePhoneEnabled =
        giftOrganizerRequirement !== UserDetailsRequirement.OPTIONAL_NO_CONFIRMATION && isPurchasePhoneRequired(seller);
    const isPhoneRequired = requirePhoneEnabled ? phoneRequired : null;

    const disabled = !!organizer;
    const isPhoneSwitchedOff = giftOrganizerRequirement !== null && isPhoneRequired !== null && !phoneRequired;

    const handleOnChange = (e: any) => {
        helpers.setValue(e.target.value);
    };

    useEffect(() => {
        if (organizer?.phone) {
            helpers.setValue(organizer.phone);
        }
        // Not adding helpers in the dependency array as it is coming from formik and will always change even when some other field changes and it can cause infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizer?.phone]);

    useEffect(() => {
        useCustomerDetailsStore.setState({
            phoneRequired: requirePhoneEnabled ? phoneRequired : false,
        });
    }, [requirePhoneEnabled, phoneRequired]);

    useEffect(() => {
        debouncedSyncGuestFormResponse(FieldType.PHONE, field.value, form);
    }, [field.value, form]);

    return (
        <FormField
            preventLabelClick
            name="phone"
            label={
                <CustomerPhoneLabel isRequired={isPhoneRequired} isDisabled={disabled} isChecked={!!phoneRequired} />
            }
        >
            <div className="flex items-center space-x-2">
                <Input
                    value={field.value}
                    disabled={disabled || isPhoneSwitchedOff}
                    name="phone"
                    placeholder="Phone Number"
                    isRequired={isPhoneRequired}
                    onChange={handleOnChange}
                />

                {disabled && (
                    <SecondaryButton icon={<EditIcon />} onClick={onEditContactClick}>
                        Edit
                    </SecondaryButton>
                )}
            </div>
        </FormField>
    );
});

interface CustomerPhoneLabelProps {
    isChecked: boolean;
    isRequired: boolean | null;
    isDisabled: boolean;
}

const CustomerPhoneLabel = ({ isRequired, isChecked, isDisabled }: CustomerPhoneLabelProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [field, meta, helpers] = useField("phone");

    const handleChange = (phoneRequired: boolean) => {
        useCustomerDetailsStore.getState().actions.setPhoneRequired(phoneRequired);

        if (!phoneRequired) {
            helpers.setValue("");
            helpers.setError("");
            useCustomerDetailsStore.setState({ phone: "" });
        }
    };

    if (isRequired === null || isDisabled) {
        return <>Phone Number</>;
    }

    return (
        <SwitchField size="small" font="font-bold" direction="left" isChecked={isChecked} onChange={handleChange}>
            <span className="font-bold">Phone Number</span>
        </SwitchField>
    );
};

CreatePurchasePhoneNumber.displayName = "CreatePurchasePhoneNumber";
